import { DateOnly } from '@if/core/date'
import { EPaymentMethod } from '@src/types/constants'
import { EditPaymentSchemaType } from './EditPayments-schema'

export enum EEditPaymentRowMark {
  Modified = 'Modified',
  Added = 'Added',
  Removed = 'Removed',
}

export const EDIT_PAYMENT_PROVENANCE_FIELDS = [
  'effectiveDate',
  'amount',
  'paymentMethod',
  'note',
  'sendSmsReminder',
] as const

export type EditPaymentProvenanceField = (typeof EDIT_PAYMENT_PROVENANCE_FIELDS)[number]

export type EditPaymentProvenanceValue = DateOnly | number | EPaymentMethod | string | boolean | null | undefined

export interface EditPaymentFieldChange {
  field: EditPaymentProvenanceField
  before: EditPaymentProvenanceValue
  after: EditPaymentProvenanceValue
}

export interface EditPaymentRowProvenance {
  mark: EEditPaymentRowMark
  changes: EditPaymentFieldChange[]
  openedAs?: EditPaymentSchemaType
}

export type EditPaymentBaseline = ReadonlyMap<string, EditPaymentSchemaType>

export const buildEditPaymentBaseline = (payments: readonly EditPaymentSchemaType[]): EditPaymentBaseline =>
  new Map(payments.map((payment) => [payment.id, payment]))

const isSameValue = (
  field: EditPaymentProvenanceField,
  before: EditPaymentProvenanceValue,
  after: EditPaymentProvenanceValue,
): boolean => {
  if (field === 'effectiveDate') return (before as DateOnly | null)?.getTime() === (after as DateOnly | null)?.getTime()
  if (field === 'note') return (before ?? '') === (after ?? '')
  return before === after
}

export const getEditPaymentRowProvenance = (
  payment: EditPaymentSchemaType,
  baseline: EditPaymentBaseline,
): EditPaymentRowProvenance | null => {
  const openedAs = baseline.get(payment.id)
  if (!openedAs) return { mark: EEditPaymentRowMark.Added, changes: [] }
  if (payment.isDeleted) return { mark: EEditPaymentRowMark.Removed, changes: [], openedAs }

  const changes = EDIT_PAYMENT_PROVENANCE_FIELDS.reduce<EditPaymentFieldChange[]>((acc, field) => {
    if (!isSameValue(field, openedAs[field], payment[field]))
      acc.push({ field, before: openedAs[field], after: payment[field] })
    return acc
  }, [])

  return changes.length > 0 ? { mark: EEditPaymentRowMark.Modified, changes, openedAs } : null
}
